// src/pages/calendar/index.js
import React, { useState, useEffect, useCallback, useRef } from "react";
import FullCalendar from "@fullcalendar/react";
import dayGridPlugin from "@fullcalendar/daygrid";
import timeGridPlugin from "@fullcalendar/timegrid";
import interactionPlugin from "@fullcalendar/interaction";
import esLocale from "@fullcalendar/core/locales/es";
import { Card, CircularProgress, Backdrop } from "@mui/material";

import DashboardLayout from "common/LayoutContainers/DashboardLayout";
import DashboardNavbar from "common/Navbars/DashboardNavbar";
import Footer from "common/Footer";
import SuiBox from "components/SuiBox";

import CalendarEventForm from "./components/calendarEventForm";
import CalendarEventDetails from "./components/calendarEventDetails";
import CalendarApi from "api/calendarApi";

import useUserRoles from "hooks/useUserRoles";

const Calendar = () => {
  const [events, setEvents] = useState([]);
  const [selectedEvent, setSelectedEvent] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isNewEvent, setIsNewEvent] = useState(false);
  const [isEditMode, setIsEditMode] = useState(false);
  const [loading, setLoading] = useState(false);
  const [currentRange, setCurrentRange] = useState(null);
  const { user } = useUserRoles();

  // Ref para evitar múltiples llamadas simultáneas
  const fetchingRef = useRef(false);
  // Cache de eventos cargados por rango
  const eventsCacheRef = useRef(new Map());

  /**
   * Calcula el rango de fechas con márgenes para pre-cargar datos
   */
  const calculateDateRange = useCallback((info) => {
    const start = new Date(info.start);
    const end = new Date(info.end);

    // Agregar margen de 1 mes antes y después para scroll suave
    start.setMonth(start.getMonth() - 1);
    end.setMonth(end.getMonth() + 1);

    return {
      start: start.toISOString(),
      end: end.toISOString(),
      key: `${start.toISOString()}_${end.toISOString()}`,
    };
  }, []);

  /**
   * Convierte fecha a formato ISO si no lo está
   */
  const ensureISOFormat = (dateString) => {
    if (!dateString) return null;
    const date = new Date(dateString);
    return isNaN(date.getTime()) ? null : date.toISOString();
  };

  /**
   * Obtiene eventos del rango de fechas visible
   */
  const fetchEvents = useCallback(
    async (dateInfo) => {
      if (fetchingRef.current) return;

      const range = calculateDateRange(dateInfo);

      // DEBUG: Limpiar caché para forzar recarga
      eventsCacheRef.current.clear();
      // Verificar si ya tenemos estos datos en caché
      // if (eventsCacheRef.current.has(range.key)) {
      //   setEvents(eventsCacheRef.current.get(range.key));
      //   setCurrentRange(range);
      //   return;
      // }

      fetchingRef.current = true;
      setLoading(true);

      try {
        const response = await CalendarApi.getEvents(range.start, range.end);
        // Manejar respuesta paginada (con results) o sin paginar (array directo)
        const eventsData = response.data.results || response.data;
        // DEBUG: Ver qué viene del backend
        console.log("Eventos del backend:", eventsData);
        const formattedEvents = eventsData.map((event) => ({
          id: event.id,
          title: event.title,
          start: ensureISOFormat(event.start_time),
          end: ensureISOFormat(event.end_time),
          allDay: event.is_all_day,
          backgroundColor: getPriorityColor(event.priority),
          borderColor: getPriorityColor(event.priority),
          extendedProps: {
            description: event.description,
            priority: event.priority,
            status: event.status,
            location: event.location,
            // Manejar formato nuevo (optimizado) y viejo (completo)
            assignedTo: event.assigned_to || {
              identificacion: event.assigned_to_id,
              nombre_completo: event.assigned_to_nombre,
            },
            createdBy: event.created_by || {
              identificacion: event.created_by_id,
              nombre_completo: event.created_by_nombre,
            },
            // Audiencia vinculada (si existe)
            audiencia_id: event.audiencia_id,
          },
        }));

        // Guardar en caché
        eventsCacheRef.current.set(range.key, formattedEvents);
        setEvents(formattedEvents);
        setCurrentRange(range);
      } catch (error) {
        console.error("Error fetching events:", error);
      } finally {
        setLoading(false);
        fetchingRef.current = false;
      }
    },
    [calculateDateRange, user]
  );

  const getPriorityColor = (priority) => {
    const colors = {
      low: "#4CAF50",
      medium: "#FFC107",
      high: "#F44336",
    };
    return colors[priority] || colors.medium;
  };

  /**
   * Handler cuando cambia el rango visible del calendario
   */
  const handleDatesSet = useCallback(
    (dateInfo) => {
      fetchEvents(dateInfo);
    },
    [fetchEvents]
  );

  const handleDateSelect = (selectInfo) => {
    setSelectedEvent({
      start: selectInfo.startStr,
      end: selectInfo.endStr,
      allDay: selectInfo.allDay,
    });
    setIsNewEvent(true);
    setIsEditMode(false);
    setIsModalOpen(true);
  };

  const handleEventClick = (clickInfo) => {
    setSelectedEvent(clickInfo.event);
    setIsNewEvent(false);
    setIsEditMode(false);
    setIsModalOpen(true);
  };

  /**
   * Handler para cambiar a modo edición desde el modal de detalles
   */
  const handleEditEvent = () => {
    setIsEditMode(true);
  };

  /**
   * Optimizado: usa PATCH para solo actualizar fechas
   */
  const handleEventDrop = async (eventDropInfo) => {
    const eventId = eventDropInfo.event.id;
    const newStart = eventDropInfo.event.start.toISOString();
    const newEnd = eventDropInfo.event.end ? eventDropInfo.event.end.toISOString() : newStart;

    try {
      await CalendarApi.updateEventDates(eventId, newStart, newEnd);

      // Actualización optimista: actualizar en caché sin recargar
      setEvents((prevEvents) =>
        prevEvents.map((event) =>
          event.id === eventId ? { ...event, start: newStart, end: newEnd } : event
        )
      );

      // Actualizar caché
      if (currentRange) {
        eventsCacheRef.current.set(
          currentRange.key,
          events.map((event) =>
            event.id === eventId ? { ...event, start: newStart, end: newEnd } : event
          )
        );
      }
    } catch (error) {
      console.error("Error updating event:", error);
      eventDropInfo.revert();
    }
  };

  /**
   * Handler optimizado para crear/actualizar eventos
   */
  const handleEventSubmit = async (eventData) => {
    try {
      let savedEvent;
      if (isNewEvent) {
        const response = await CalendarApi.createEvent(eventData);
        savedEvent = response.data;

        // Agregar el nuevo evento al estado
        const formattedEvent = {
          id: savedEvent.id,
          title: savedEvent.title,
          start: ensureISOFormat(savedEvent.start_time),
          end: ensureISOFormat(savedEvent.end_time),
          allDay: savedEvent.is_all_day,
          backgroundColor: getPriorityColor(savedEvent.priority),
          borderColor: getPriorityColor(savedEvent.priority),
          extendedProps: {
            description: savedEvent.description,
            priority: savedEvent.priority,
            status: savedEvent.status,
            location: savedEvent.location,
            assignedTo: savedEvent.assigned_to,
            createdBy: savedEvent.created_by,
          },
        };

        setEvents((prevEvents) => [...prevEvents, formattedEvent]);

        // Actualizar caché
        if (currentRange) {
          eventsCacheRef.current.set(currentRange.key, [...events, formattedEvent]);
        }
      } else {
        const response = await CalendarApi.updateEvent(selectedEvent.id, eventData);
        savedEvent = response.data;
        // Actualizar el evento en el estado
        setEvents((prevEvents) =>
          prevEvents.map((event) =>
            event.id === selectedEvent.id
              ? {
                  ...event,
                  title: savedEvent.title,
                  start: ensureISOFormat(savedEvent.start_time),
                  end: ensureISOFormat(savedEvent.end_time),
                  allDay: savedEvent.is_all_day,
                  backgroundColor: getPriorityColor(savedEvent.priority),
                  borderColor: getPriorityColor(savedEvent.priority),
                  extendedProps: {
                    description: savedEvent.description,
                    priority: savedEvent.priority,
                    status: savedEvent.status,
                    location: savedEvent.location,
                    assignedTo: savedEvent.assigned_to,
                    createdBy: savedEvent.created_by,
                  },
                }
              : event
          )
        );

        // Actualizar caché
        if (currentRange) {
          const updatedEvents = events.map((event) =>
            event.id === selectedEvent.id
              ? {
                  ...event,
                  title: savedEvent.title,
                  start: ensureISOFormat(savedEvent.start_time),
                  end: ensureISOFormat(savedEvent.end_time),
                }
              : event
          );
          eventsCacheRef.current.set(currentRange.key, updatedEvents);
        }
      }

      setIsModalOpen(false);
      setIsEditMode(false);
    } catch (error) {
      console.error("Error saving event:", error);
    }
  };

  /**
   * Handler optimizado para eliminar eventos
   */
  const handleEventDelete = async (eventId) => {
    try {
      await CalendarApi.deleteEvent(eventId);

      // Eliminar del estado
      setEvents((prevEvents) => prevEvents.filter((event) => event.id !== eventId));

      // Actualizar caché
      if (currentRange) {
        const filteredEvents = events.filter((event) => event.id !== eventId);
        eventsCacheRef.current.set(currentRange.key, filteredEvents);
      }

      setIsModalOpen(false);
      setIsEditMode(false);
    } catch (error) {
      console.error("Error deleting event:", error);
    }
  };

  /**
   * Limpiar caché cuando el componente se desmonta
   */
  useEffect(() => {
    return () => {
      eventsCacheRef.current.clear();
    };
  }, []);

  const handleCloseModal = () => {
    setIsModalOpen(false);
    setIsEditMode(false);
  };

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <SuiBox>
        <Card>
          <SuiBox p={2}>
            <FullCalendar
              plugins={[dayGridPlugin, timeGridPlugin, interactionPlugin]}
              headerToolbar={{
                left: "prev,next today",
                center: "title",
                right: "dayGridMonth,timeGridWeek,timeGridDay",
              }}
              initialView="dayGridMonth"
              editable={true}
              selectable={true}
              selectMirror={true}
              dayMaxEvents={true}
              weekends={true}
              events={events}
              select={handleDateSelect}
              eventClick={handleEventClick}
              eventDrop={handleEventDrop}
              datesSet={handleDatesSet}
              locale={esLocale}
              height="auto"
              contentHeight="auto"
              slotMinTime="06:00:00"
              slotMaxTime="21:00:00"
              eventTimeFormat={{
                hour: "2-digit",
                minute: "2-digit",
                meridiem: false,
                hour12: false,
              }}
              displayEventTime={true}
              loading={(isLoading) => setLoading(isLoading)}
            />
          </SuiBox>
        </Card>

        {isModalOpen &&
          (isNewEvent || isEditMode ? (
            <CalendarEventForm
              isOpen={isModalOpen}
              onClose={handleCloseModal}
              selectedDate={selectedEvent}
              onSubmit={handleEventSubmit}
              isEditMode={isEditMode}
            />
          ) : (
            <CalendarEventDetails
              isOpen={isModalOpen}
              onClose={handleCloseModal}
              event={selectedEvent}
              onEdit={handleEditEvent}
              onDelete={handleEventDelete}
            />
          ))}
      </SuiBox>

      {/* Loading overlay */}
      <Backdrop sx={{ color: "#fff", zIndex: (theme) => theme.zIndex.drawer + 1 }} open={loading}>
        <CircularProgress color="inherit" />
      </Backdrop>

      <Footer />
    </DashboardLayout>
  );
};

export default Calendar;
